import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GeneralFields } from "@/components/GeneralFields";
import { TyreWidget, UnitToggle } from "@/components/TyreWidget";
import { ConditionsFields, fetchEventWeather } from "@/components/ConditionsFields";
import { StintTyreFields } from "@/components/StintTyreFields";
import { TyreModelFields } from "@/components/TyreModelFields";
import { PressureAnalysisPanel } from "@/components/PressureAnalysisPanel";
import { LapsTab } from "@/components/LapsTab";
import { TelemetryTab } from "@/components/telemetry/TelemetryTab";
import { SetupGroup, GearFields } from "@/components/SetupFields";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { emptySession, FRONT_FIELDS, REAR_FIELDS, GENERAL_SETUP_FIELDS } from "@/lib/format";
import { Textarea } from "@/components/ui/textarea";
import { Field, fieldInputCls } from "@/components/Field";
import { useGarage } from "@/lib/useGarage";

const TABS = ["general", "tyres", "setup", "laps", "telemetry"];

const SetupBlock = ({ title, testid, children }) => (
  <section data-testid={testid} className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-4">
    <h3 className="font-heading uppercase font-bold text-lg mb-3 flex items-center gap-2"><span className="w-1 h-4 bg-[#FF3B30]" />{title}</h3>
    {children}
  </section>
);

function toForm(s) {
  const base = emptySession();
  return {
    ...base,
    ...Object.fromEntries(Object.keys(base).map((k) => [k, s[k] ?? base[k]])),
    front: { ...base.front, ...s.front },
    setup_general: { ...base.setup_general, ...s.setup_general },
    rear: { ...base.rear, ...s.rear },
    gear: { ...base.gear, ...s.gear },
    tyres: { ...base.tyres, ...s.tyres },
    tyres2: { ...base.tyres2, ...s.tyres2 },
    conditions: { ...base.conditions, ...s.conditions },
  };
}

function toPayload(form) {
  const num = (v) => (v === null || v === "" || v === undefined ? null : Number(v));
  const conv = (ty) => Object.fromEntries(Object.entries(ty).map(([k, v]) => [k, { cold: num(v.cold), hot: num(v.hot) }]));
  const tyres = conv(form.tyres);
  const tyres2 = conv(form.tyres2);
  const c = form.conditions;
  const conditions = { weather: c.weather || "", air_temp: num(c.air_temp), track_temp: num(c.track_temp), humidity: num(c.humidity), wind_speed: num(c.wind_speed), wind_deg: num(c.wind_deg), pressure: num(c.pressure) };
  return {
    ...form,
    laps: form.laps === "" || form.laps == null ? null : parseInt(form.laps, 10),
    laps2: form.laps2 === "" || form.laps2 == null ? null : parseInt(form.laps2, 10),
    gear: { sprocket: String(form.gear.sprocket ?? ""), pinion: String(form.gear.pinion ?? "") }, tyres, tyres2, conditions,
  };
}

export default function SessionForm() {
  const { eventId, sessionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const { garage } = useGarage();
  const [form, setForm] = useState(() => (state?.copyFrom ? { ...toForm(state.copyFrom), name: `${state.copyFrom.name} (2)` } : emptySession()));
  const [event, setEvent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tyreSets, setTyreSets] = useState([]);
  const [tab, setTab] = useState(state?.tab || "general");
  const [wxInfo, setWxInfo] = useState(null);

  useEffect(() => {
    api.get("/tyre-sets").then((r) => setTyreSets(r.data));
  }, []);

  useEffect(() => {
    api.get(`/events/${eventId}`).then((r) => setEvent(r.data)).catch(() => navigate("/events"));
    if (sessionId) {
      api.get(`/sessions/${sessionId}`).then((r) => setForm(toForm(r.data))).catch(() => navigate(`/events/${eventId}`));
    }
  }, [eventId, sessionId, navigate]);

  useEffect(() => {
    if (sessionId) return;
    fetchEventWeather(eventId, lang).then((w) => {
      if (!w?.live || !w.conditions) return;
      setForm((f) => ({ ...f, conditions: { ...f.conditions, ...w.conditions } }));
      setWxInfo(w);
      toast.success(t("weather_filled"));
    }).catch(() => {});
  }, [eventId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(`${t("session_name")}: ${t("required")}`);
      return;
    }
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (sessionId) {
        await api.put(`/sessions/${sessionId}`, payload);
        toast.success(t("saved"));
        navigate(`/events/${eventId}`);
      } else {
        const { data } = await api.post(`/events/${eventId}/sessions`, payload);
        toast.success(t("session_created_telemetry"));
        setTab("telemetry");
        navigate(`/events/${eventId}/sessions/${data.id}`, { replace: true, state: { tab: "telemetry" } });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sectionSetter = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <form onSubmit={submit} noValidate data-testid="session-form" className="fade-up">
      <Link to={`/events/${eventId}`} data-testid="back-to-event-link" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-slate-500 hover:text-white mb-5 transition-colors duration-200">
        <ArrowLeft className="w-3.5 h-3.5" /> {event?.name}
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <h1 data-testid="session-form-title" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl tracking-tight leading-none">
          {sessionId ? t("edit_session") : t("new_session")}
        </h1>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" data-testid="session-cancel-btn" onClick={() => navigate(`/events/${eventId}`)}>{t("cancel")}</Button>
          <Button type="submit" disabled={busy} data-testid="session-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-lg h-11 px-6 transition-transform duration-150 active:scale-95">
            <Save className="w-4 h-4 mr-2" /> {t("save")}
          </Button>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto bg-transparent border-b border-[#282D3D] rounded-none h-auto p-0 gap-1">
          {TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab} data-testid={`session-tab-${tab}`}
              className="font-heading uppercase tracking-wide text-base sm:text-lg rounded-none border-b-2 border-transparent px-4 py-2.5 text-slate-500 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-[#FF3B30] data-[state=active]:shadow-none">
              {t(tab)}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="bg-[#12141C]/60 border border-t-0 border-[#282D3D] rounded-b-sm p-4 sm:p-6">
          <TabsContent value="general" className="mt-0"><GeneralFields form={form} setForm={setForm} garage={garage} event={event} /></TabsContent>
          <TabsContent value="tyres" className="mt-0 space-y-6">
            <ConditionsFields values={form.conditions} onChange={sectionSetter("conditions")} eventId={eventId} info={wxInfo} setInfo={setWxInfo} />
            <UnitToggle unit={form.pressure_unit} onChange={sectionSetter("pressure_unit")} />
            {(form.pit ? [1, 2] : [1]).map((n) => (
              <SetupBlock key={n} title={form.pit ? `${t(`stint${n}`)} · ${t("tyres")}` : t("tyres")} testid={`stint-${n}-block`}>
                <div className="space-y-4">
                  <StintTyreFields n={n} form={form} setForm={setForm} sets={tyreSets} eventId={eventId} />
                  <TyreModelFields n={n} form={form} setForm={setForm} sets={tyreSets} />
                  <TyreWidget prefix={n === 1 ? "tyre" : "tyre2"} tyres={n === 1 ? form.tyres : form.tyres2} unit={form.pressure_unit} onChange={sectionSetter(n === 1 ? "tyres" : "tyres2")} />
                </div>
              </SetupBlock>
            ))}
            <PressureAnalysisPanel form={form} track={event?.track} sessionId={sessionId} />
          </TabsContent>
          <TabsContent value="laps" className="mt-0"><LapsTab form={form} setForm={setForm} /></TabsContent>
          <TabsContent value="telemetry" className="mt-0"><TelemetryTab sessionId={sessionId} pit={form.pit} setForm={setForm} trackId={form.track_id} lapData={form.lap_data} lapData2={form.lap_data2} /></TabsContent>
          <TabsContent value="setup" className="mt-0 space-y-5">
            <SetupBlock title={t("setup_general")} testid="setup-group-general">
              <div className="space-y-4">
                <Field label={t("session_notes")}>
                  <Textarea data-testid="session-notes-input" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${fieldInputCls} h-auto font-sans`} />
                </Field>
                <SetupGroup section="general" prefix="g" fields={GENERAL_SETUP_FIELDS} values={form.setup_general} onChange={sectionSetter("setup_general")} />
              </div>
            </SetupBlock>
            <SetupBlock title={t("front")} testid="setup-group-front"><SetupGroup section="front" prefix="f" fields={FRONT_FIELDS} values={form.front} onChange={sectionSetter("front")} /></SetupBlock>
            <SetupBlock title={t("rear")} testid="setup-group-rear"><SetupGroup section="rear" prefix="r" fields={REAR_FIELDS} values={form.rear} onChange={sectionSetter("rear")} /></SetupBlock>
            <SetupBlock title={t("gear")} testid="setup-group-gear"><GearFields values={form.gear} onChange={sectionSetter("gear")} /></SetupBlock>
          </TabsContent>
        </div>
      </Tabs>
    </form>
  );
}
